import React, {
  useRef,
  useState,
  useEffect,
  forwardRef,
  useImperativeHandle,
} from 'react';
import { AudioData, AudioSegment } from '../types';

interface AudioPlayerProps {
  audioData: AudioData | null;
  segments: AudioSegment[];
  currentSegment: AudioSegment | null;
  onTimeUpdate: (currentTime: number) => void;
  onSegmentClick: (segment: AudioSegment) => void;
}

export interface AudioPlayerRef {
  playSegment: (segment: AudioSegment) => void;
}

const AudioPlayer = forwardRef<AudioPlayerRef, AudioPlayerProps>(
  (
    { audioData, segments, currentSegment, onTimeUpdate, onSegmentClick },
    ref
  ) => {
    const audioRef = useRef<HTMLAudioElement>(null);
    const [isPlaying, setIsPlaying] = useState(false);
    const [currentTime, setCurrentTime] = useState(0);
    const [duration, setDuration] = useState(0);
    const segmentTimeoutRef = useRef<NodeJS.Timeout | null>(null);

    // Reset player state when audioData changes
    useEffect(() => {
      if (audioData) {
        setCurrentTime(0);
        setIsPlaying(false);
        setDuration(0);
      }
    }, [audioData]);

    useEffect(() => {
      const audio = audioRef.current;
      if (!audio || !audioData) return;

      const handleTimeUpdate = () => {
        const time = audio.currentTime;
        setCurrentTime(time);
        onTimeUpdate(time);
      };

      const handleLoadedMetadata = () => {
        setDuration(audio.duration);
      };

      const handleEnded = () => {
        setIsPlaying(false);
      };

      const handleCanPlay = () => {
        if (audio.duration && !isNaN(audio.duration)) {
          setDuration(audio.duration);
        }
      };

      audio.addEventListener('timeupdate', handleTimeUpdate);
      audio.addEventListener('loadedmetadata', handleLoadedMetadata);
      audio.addEventListener('canplay', handleCanPlay);
      audio.addEventListener('ended', handleEnded);

      // Try to get duration immediately if already loaded
      if (audio.duration && !isNaN(audio.duration)) {
        setDuration(audio.duration);
      }

      return () => {
        audio.removeEventListener('timeupdate', handleTimeUpdate);
        audio.removeEventListener('loadedmetadata', handleLoadedMetadata);
        audio.removeEventListener('canplay', handleCanPlay);
        audio.removeEventListener('ended', handleEnded);

        // Clear segment timeout on cleanup
        if (segmentTimeoutRef.current) {
          clearTimeout(segmentTimeoutRef.current);
          segmentTimeoutRef.current = null;
        }
      };
    }, [onTimeUpdate, audioData, currentSegment]);

    const togglePlayPause = async () => {
      if (!audioRef.current) return;

      try {
        if (isPlaying) {
          audioRef.current.pause();
          setIsPlaying(false);
          // Clear segment timeout when manually pausing
          if (segmentTimeoutRef.current) {
            clearTimeout(segmentTimeoutRef.current);
            segmentTimeoutRef.current = null;
          }
        } else {
          await audioRef.current.play();
          setIsPlaying(true);
        }
      } catch (error) {
        console.error('Error toggling play/pause:', error);
        setIsPlaying(false);
      }
    };

    const seekTo = (time: number) => {
      if (!audioRef.current) return;
      audioRef.current.currentTime = time;
      setCurrentTime(time);
    };

    const playSegment = async (segment: AudioSegment) => {
      if (!audioRef.current) return;

      try {
        // Clear any existing segment timeout
        if (segmentTimeoutRef.current) {
          clearTimeout(segmentTimeoutRef.current);
          segmentTimeoutRef.current = null;
        }

        // First select the segment
        onSegmentClick(segment);

        // Seek to segment start and wait for the seek to complete
        audioRef.current.currentTime = segment.startTime;

        // Wait a small amount to ensure seeking is complete
        await new Promise((resolve) => setTimeout(resolve, 50));

        // Start playback
        await audioRef.current.play();
        setIsPlaying(true);

        // Set timeout to pause at segment end with a bit more precision
        const segmentDuration = Math.max(
          100,
          (segment.endTime - segment.startTime) * 1000
        ); // At least 100ms
        segmentTimeoutRef.current = setTimeout(() => {
          if (audioRef.current && !audioRef.current.paused) {
            audioRef.current.pause();
            setIsPlaying(false);
          }
          segmentTimeoutRef.current = null;
        }, segmentDuration);
      } catch (error) {
        console.error('خطأ في تشغيل المقطع:', error);
        setIsPlaying(false);
      }
    };

    // Expose playSegment function through ref
    useImperativeHandle(
      ref,
      () => ({
        playSegment,
      }),
      [playSegment]
    );

    const formatTime = (time: number) => {
      if (!time || isNaN(time) || !isFinite(time)) {
        return '0:00';
      }
      const minutes = Math.floor(time / 60);
      const seconds = Math.floor(time % 60);
      return `${minutes}:${seconds.toString().padStart(2, '0')}`;
    };

    if (!audioData) {
      return (
        <div className='audio-section'>
          <p>No audio file loaded</p>
        </div>
      );
    }

    return (
      <div className='audio-section'>
        <h3>مشغل الصوت</h3>
        <audio
          ref={audioRef}
          src={audioData.url}
          preload='metadata'
        />

        <div style={{ marginBottom: '20px' }}>
          <button
            onClick={togglePlayPause}
            className='btn btn-primary'
          >
            {isPlaying ? 'إيقاف' : 'تشغيل'}
          </button>
          <span style={{ marginRight: '20px' }}>
            {formatTime(currentTime)} / {formatTime(duration)}
          </span>
        </div>

        <div className='waveform-container'>
          <div
            style={{
              width: '100%',
              height: '100%',
              position: 'relative',
              direction: 'rtl',
            }}
          >
            <div
              style={{
                width: '100%',
                height: '4px',
                backgroundColor: 'blue',
                position: 'absolute',
                top: '50%',
                transform: 'translateY(-50%)',
                cursor: 'pointer',
              }}
              onClick={(e) => {
                const rect = e.currentTarget.getBoundingClientRect();
                const x = rect.right - e.clientX; // Calculate from right for RTL
                const percentage = x / rect.width;
                const time = percentage * duration;
                seekTo(time);
              }}
            >
              <div
                style={{
                  width: `${
                    // duration > 0 ? (currentTime / duration) * 100 : 0 // reverse calculation for RTL
                    100 - (currentTime / duration) * 100
                  }%`,
                  height: '100%',
                  backgroundColor: 'grey',
                  translate: '0 0',
                  marginRight: 'auto', // Progress should grow from right in RTL
                }}
              />
            </div>

            {duration > 0 &&
              segments.map((segment) => (
                <div
                  key={segment.id}
                  style={{
                    position: 'absolute',
                    right: `${(segment.startTime / duration) * 100}%`, // Position from right edge for RTL timeline
                    width: `${Math.max(
                      0.5,
                      ((segment.endTime - segment.startTime) / duration) * 100
                    )}%`, // Minimum width
                    height: '24px',
                    backgroundColor:
                      currentSegment?.id === segment.id ? '#ff4757' : '#2ed573',
                    opacity: currentSegment?.id === segment.id ? 0.9 : 0.7,
                    top: '50%',
                    transform: 'translateY(-50%)',
                    cursor: 'pointer',
                    border:
                      currentSegment?.id === segment.id
                        ? '2px solid #ff3742'
                        : '1px solid #fff',
                    borderRadius: '3px',
                    boxShadow:
                      currentSegment?.id === segment.id
                        ? '0 2px 4px rgba(255, 71, 87, 0.3)'
                        : 'none',
                    transition: 'all 0.2s ease-in-out',
                    zIndex: currentSegment?.id === segment.id ? 10 : 1,
                    direction: 'rtl', // Ensure RTL direction
                  }}
                  onClick={() => playSegment(segment)}
                  title={`${formatTime(segment.startTime)} - ${formatTime(
                    segment.endTime
                  )}: ${segment.text}`}
                  onMouseEnter={(e) => {
                    if (currentSegment?.id !== segment.id) {
                      e.currentTarget.style.opacity = '0.85';
                      e.currentTarget.style.transform =
                        'translateY(-50%) scale(1.05)';
                    }
                  }}
                  onMouseLeave={(e) => {
                    if (currentSegment?.id !== segment.id) {
                      e.currentTarget.style.opacity = '0.7';
                      e.currentTarget.style.transform =
                        'translateY(-50%) scale(1)';
                    }
                  }}
                />
              ))}
          </div>
        </div>
      </div>
    );
  }
);

AudioPlayer.displayName = 'AudioPlayer';

export default AudioPlayer;
